import { SalesInboxConversation } from '@dreamai/types';

const POSITIVE_CATEGORIES = [
  'schedule',
  'book',
  'success',
  'respond',
  'escalate',
];

/**
 * Determines if a conversation needs attention (awaiting our response or may want to respond)
 * This is the single source of truth for this logic, used by both the SalesInbox and navigation
 */
export const conversationNeedsAttention = (
  conversation: SalesInboxConversation,
): boolean => {
  return conversation.awaitingOurResponse || conversation.mayWantToRespond;
};

/**
 * Determines if a conversation is a positive reply
 */
export const conversationIsPositive = (
  conversation: SalesInboxConversation,
): boolean => {
  return conversation.replyCategories.some((cat) =>
    POSITIVE_CATEGORIES.includes(cat),
  );
};

/**
 * Determines if a conversation is a negative reply
 */
export const conversationIsNegative = (
  conversation: SalesInboxConversation,
): boolean => {
  return conversation.isNegative;
};

/**
 * Counts the number of conversations that need attention
 */
export const countNeedsAttention = (
  conversations: SalesInboxConversation[],
): number => {
  return conversations.filter(conversationNeedsAttention).length;
};

/**
 * Counts the number of positive conversations
 */
export const countPositive = (
  conversations: SalesInboxConversation[],
): number => {
  return conversations.filter(conversationIsPositive).length;
};

/**
 * Counts the number of negative conversations
 */
export const countNegative = (
  conversations: SalesInboxConversation[],
): number => {
  return conversations.filter(conversationIsNegative).length;
};

/**
 * Gets all filter counts for the conversations
 */
export const getFilterCounts = (
  conversations: SalesInboxConversation[],
): {
  needsAttention: number;
  positive: number;
  negative: number;
  all: number;
} => {
  return {
    needsAttention: countNeedsAttention(conversations),
    positive: countPositive(conversations),
    negative: countNegative(conversations),
    all: conversations.length,
  };
};
